import { US_STATE_CODES } from './us_state_codes.js';

import { date_range_extractor } from './date_range_extractor.js';

import { recognized_officers } from './recognized_officers.js';

// A function to clean a raw text buffer containing Sacred Harp
// Minutes markup so it meets the required "standards" and handles
// many of the problems we have seen when receiving files from
// secretaries.

export function clean_minutes_markup(text) {
  // ==> {text: "...", start_date: <date or null>, end_date: <date or null>}
  // Standardize line endings of any incoming format to Unix \n; temporarily hide newlines by encoding as <newline>.
  // Standardize all other runs of (non-newline) whitespace to be just a single space.
  // This takes care of tabs, non-breaking spaces, control chars and other weird whitespace.

  text = text.replace(/\u2028/g, '\n'); // Special case of soft-returns as "Line Separator" Unicode 2028 / UTF8: e280a8 from some programs, e.g. Pages
  text = text.replace(/(?:(?:\x0D\x0A)|[\x0D\x0A])/g, '<newline>');
  text = text.replace(/[\x00-\x1F\s]+/g, ' ');
  text = text.replace(/<newline>/g, '\n');

  // Remove all runs of spaces at beginnings and ends of all lines.

  text = text.replace(/^ /gm, '');
  text = text.replace(/ $/gm, '');

  // Remove leading and trailing whitespace, newlines, and/or lines of dashes from entire buffer.

  text = text.replace(/^[-\s]+/, '');
  text = text.replace(/[-\s]+$/, '');

  // Add back a single blank line at very end.

  text = text + '\n';

  // Maximum of two consecutive newlines in remaining text.

  text = text.replace(/\n\n+/g, '\n\n');

  // If the entire thing happens to be double-spaced, assume that's wrong, and single-space it.
  // Define double-spaced as: every line is less than 100 chars; two returns after every line.
  // The affected text will then get unwrapped in the subsequent step because unwrapping looks at

  if (text.match(/(.{0,100}\n\n){10,}/)) {
    text = text.replace(/\n\n/g, '\n');
  }

  // PREPARATION FOR UNWRAPPING:

  // Protect the first three lines from being unwrapped by ensuring 2 newlines separating them.

  text = text.replace(/^(.+)\n+(.+)\n+(.+)\n+/, '$1\n\n$2\n\n$3\n\n');

  // Except in the initial headings, If there are 2 adjacent lines and the the prior line ends with
  // punctuation other than a period, assume it was meant to be 1 line and
  // replace the newline wth a space.

  text = text.replace(/(\w)([,();:-]+)(\n)(\w)/gm, '$1$2 $4');

  // Protect any short lines starting with an upper-case character
  // and not ending in a period (dates, lunch recess, etc). from
  // being unwrapped.

  text = text.replace(/^([A-Z].{1,50}[^.])$/gm, '\n$1\n');

  // Assume that any period at the end of a line signals the end of
  // a paragraph.  This will result in breaking some true paragraphs
  // during the unwrapping step, but that is better than not doing
  // the unwrapping at all.

  text = text.replace(/\.$/gm, '.\n');

  // Re-clean any multiple newlines or initial whitespace possibly added by the unwrapping prep steps...

  text = text.replace(/^[-\s]+/, '');
  text = text.replace(/\n\n+/g, '\n\n');

  // UNWRAPPING:

  // Unwrap long paragraphs (convert all \n in a sequence of single-spaced lines, to spaces; then add back an \n at end).
  // Note that this step also results in the (desired) overall double-spacing of the buffer.

  text = text.replace(/((^.+\n)+)/gm, function(m) {
    return m.replace(/\n/g, ' ') + '\n';
  });

  // RESTORATION FROM UNWRAPPING:

  // Remove the temporary/unintended double-newlines between 1st 3 header lines.

  text = text.replace(/^(.+)\n+(.+)\n+(.+)/, '$1\n$2\n$3');

  // Intra-paragraph cleanups from this point onward:

  // Dumb down certain non-ascii characters.  This standardizes to
  // our ascii-based markup format which is then easier to clean up
  // in later steps.

  text = text.replace(/__/g, '--'); // 2 underscores (error sometimes made)
  text = text.replace(/–/g, '--'); // N-dash - often put in by some Microsoft mail programs where long dash is meant
  text = text.replace(/—/g, '--'); // M-Dash or long-dash
  text = text.replace(/‘/g, "'"); // curly left single quote
  text = text.replace(/’/g, "'"); // curly right single quote aka apostrophe
  text = text.replace(/“/g, '"'); // curly left double quote
  text = text.replace(/”/g, '"'); // curly right double quote

  // No spaces before punctuation.

  text = text.replace(/ +([.,:;]) */g, '$1');

  // Repeated punctuation; except repeated periods are OK.

  text = text.replace(/([,:;])\1/g, '$1');

  // Exactly one space after each colon, comma, or semicolon; and two after periods, unless followed by a quote mark or another period.
  // Note: this step might introduce extra spaces at end of paragraphs.

  text = text.replace(/([,:;]) *(?!['"])/g, '$1 ');
  text = text.replace(/([.]) *(?!['".])/g, '$1  ');

  // Space required before left paren.  No spaces after left or before right.

  text = text.replace(/([^ ])(\()/g, '$1 $2');
  text = text.replace(/\( +/g, '(');
  text = text.replace(/ +\)/g, ')');

  // Special case: "a.m.", "p.m", or initials, such as "J.K."

  text = text.replace(/\b(\w\.) {2}\b(\w\.) {2}/g, '$1$2 ');

  // Special case: colons in times like 10:00 a.m.

  text = text.replace(/(\d:) (\d)/g, '$1$2');

  // (Again): Remove spaces at beginnings and ends of lines in case any added by above transformations.

  text = text.replace(/^ +/gm, '');
  text = text.replace(/ +$/gm, '');

  // (Again): Clean remaining runs of spaces to single-spaces

  text = text.replace(/ +/g, ' ');

  // Semi-colons, not commas, separating singers.

  let song_number_pattern =
    '(?:\\(?[A-Z]+\\)? ?)?\\d\\d+[TBtb]?(?: ?\\(?[A-Z]+\\)?)?';
  text = text.replace(
    new RegExp(
      '(' + song_number_pattern + '), (?!' + song_number_pattern + ')([A-Z])',
      'g'
    ),
    '$1; $2'
  );

  // The editor likes double-spaces between sentences.  These will
  // be stripped out again later, in the rendering pipeline.

  text = text.replace(/(\."?) /g, '$1  ');
  text = text.replace(/\b(\w\.)(\w\.) {2}/g, '$1$2 '); // Re-fix the "a.m.", "p.m.", or "J.K." cases.
  text = text.replace(/( \w\.) {2}/g, '$1 '); // Re-fix the "T." case as in "Jennifer T." -- assume it's not end of a sentence.

  // Re-fix common abbreviated honorifics, suffixes, & titles

  text = text.replace(
    /(Mr|Mrs|Dr|Jr|Sr|St|Prof|Mt|Sgt|Maj|Rev)\. {2}/g,
    '$1. '
  );

  text = text.replace(/ ([,;:])/g, '$1'); // Never a space before a colon, semi, or comma, even if after a period.

  // Re-fix dollar or decimal amounts

  text = text.replace(/(\d+\.) {1,2}(\d+)/g, '$1$2');
  text = text.replace(/(\$\d+,) (?=\d{3})/g, '$1');

  // Lowercase the T or B after song numbers.

  text = text.replace(/(\d\d\d?[TB])/g, function(m) {
    return m.toLowerCase();
  });

  // Replace US State Postal codes at end of Location line with expanded version
  text = text.replace(/^(.+\n+)(.+\n+)(.+\n+)/, function(m, l1, l2, l3) {
    return l1 + FixStateCode(l2) + l3;
  });

  // Undo all-caps anywhere else they appear.

  text = text.replace(/([A-Z]{4,})/, function(m) {
    return FixSpecialAcronymCases(ToTitleCase(m));
  });

  // Convert "Break" to "Recess" and similar fixes.

  text = text.replace(/^BREAK[^\w\n]*$/gim, 'RECESS');

  // Upcase "Lunch" or "Recess" on a line by themselves (no punctuation; upper case).

  text = text.replace(/^(LUNCH|RECESS)[^\w\n]*$/gim, function(m, p1) {
    return p1.toUpperCase();
  });

  // Standardize the use of -- for long-dash

  text = text.replace(/ *---* */g, '--'); // Remove any spaces surrounding --; standardize length to max of 2.
  text = text.replace(/(.) *- +/g, '$1--'); // Single dash with space after (maybe in front) => long-dash, but not when at start of a line

  // Undo all-caps and force title case in all 3 title lines.

  text = text.replace(/^(((.*)\n+){3})/, function(m) {
    return FixSpecialHeadingCases(ToTitleCase(m));
  });

  // Try to standardize the format of the date in the date line.
  // If not parseable as a date or range of dates, it will be left unmodified.
  text = text.replace(/^(.+\n+.+\n+)(.+)(\n+)/, function(
    m,
    first_lines,
    l3,
    newlines
  ) {
    return first_lines + CleanDateLine(l3) + newlines;
  });

  // Find itnernal date header lines such as "Saturday [Night], November 18[th]" by making them h5 style
  // The space above the first of these will be slightly more.

  text = text.replace(/^((?:.+\n+){3})([^]*)/g, function(m, heads, body) {
    return heads + NoYearInDateHeads(body);
  });

  // Remove final punctuation (e.g. period after final officer's name)

  text = text.replace(/[^\w\n]+\n*$/, '\n');

  // Clean up officers line (at end only)

  text = text.replace(/(\n)([^\n]+)(\n*)$/, function(m, l1, l2, l3) {
    return l1 + CleanOfficersLine(l2) + l3;
  });

  // If markdown-style "level 5 run in head" marks (==) appear, ensure that there are 2 newlines before and after each.
  // Also, remove any unnecessary period(s) at the end of the head.  They are stripped out in rendering anyway.
  text = text.replace(/\n* *== *([^\n]+?)\.* *== *\n*/g, '\n\n== $1 ==\n\n');

  // One last cleanup of more than 2 newlines

  text = text.replace(/\n\n+/g, '\n\n');

  // Must end with exactly one newline.

  text = text.replace(/\n+$/, '\n');

  return text;
}

function ToTitleCase(text) {
  text = text.replace(/\b([A-Za-z])([A-Za-z]*)\b/g, function(m, p1, p2) {
    return p1.toUpperCase() + p2.toLowerCase();
  });

  // Fix some special cases

  text = text.replace(/(?!^)\b(s|a|and|the|in|to|of)\b/gi, function(m) {
    return m.toLowerCase();
  });

  // Restore names starting with Mc - McMahon, for example

  text = text.replace(/(Mc)([a-z])/g, function(m, p1, p2) {
    return p1 + p2.toUpperCase();
  });

  return text;
}

function FixStateCode(location_line) {
  location_line = location_line.replace(
    /\b([A-Z]{2})(\n*)/, // only recognize two capital letters at end of line
    function(m, state_code, newline) {
      return (US_STATE_CODES[state_code.toUpperCase()] || state_code) + newline;
    }
  );

  return location_line;
}

function NoYearInDateHeads(body) {
  // Lines such as "Saturday [Night], November 18[th]" should not have ", 2017" after them.  Also omit ordinals.

  body = body.replace(
    /^([A-Z][a-z]+( +[A-Za-z]+)?, +[A-Z][a-z]+ +[0-9]+)((st)*(nd)*(rd)*(th)*)?(, \d{4})?$/gm,
    '$1'
  );

  return body;
}

const recognized_officers_exp = '\\b(' + recognized_officers.join('|') + ')\\b';
const recognized_officers_re1 = new RegExp(
  '(?:-)' + recognized_officers_exp,
  'g'
); // dash before
const recognized_officers_re2 = new RegExp(
  recognized_officers_exp + '(?:-)',
  'g'
); // dash after

function CleanOfficersLine(oline) {
  // If there are commas but no semicolons, change them all to semicolons; otherwise assume that both are present intentionally.
  if (!oline.match(/;/)) {
    oline = oline.replace(/,/g, ';');
  }

  // If there are dashes but no double-dashes, convert those that follow officer names to double-dashes.
  if (!oline.match(/--/) && oline.match(/-/)) {
    oline = oline.replace(recognized_officers_re1, '--$1');
    oline = oline.replace(recognized_officers_re2, '$1--');
  }

  // It would be nice to detect and swap inverted officers, but that can only be done if we're sure the punctuation is correct (only -- and ;)
  // And it's a too-advanced task to try without knowing that.  (The problem being that "-" can appear in roles, names, and also erroneously in place of "--")

  // So, we only try to handle one case: where there is a perfect alternation of --'es and ;'s,
  // Then, we can examine each pair; if it seems to be "backwards", we can flip it.

  var names_and_roles = (oline + ';').split(/(--)([^;]+)(; *)/); // Split will grab a name/role, then "--", then a name/role, then ";", etc. ending with extra "".

  names_and_roles = names_and_roles.slice(0, -2); // Remove (slice away) the final ';' and the post-split empty string.

  // Return early if there are not at least 3 items (i.e. 1 pair plus the delimiter between them)
  if (names_and_roles.length < 3) {
    return oline;
  }

  // Swap any pair where the first item is not a recognized officer name, and the second item is a recognized name.
  for (let i = 0; i < names_and_roles.length / 4; i++) {
    if (
      !recognized_officers.includes(names_and_roles[i * 4 + 0]) &&
      recognized_officers.includes(names_and_roles[i * 4 + 2])
    ) {
      // swap hack: a = [ b, b = a ][0];
      names_and_roles[i * 4 + 0] = [
        names_and_roles[i * 4 + 2],
        (names_and_roles[i * 4 + 2] = names_and_roles[i * 4 + 0])
      ][0];
    }
  }

  // Re-join the items with the original delimiters still in place.
  oline = names_and_roles.join('');

  return oline;
}

function FixSpecialHeadingCases(top_lines) {
  top_lines = top_lines.replace(/^\w/gm, function(m) {
    return m.toUpperCase();
  }); // First letter of each line must always be upper case.
  top_lines = top_lines.replace(/\b(t)(he)\b/gi, function(m, p1, p2) {
    return p1.toUpperCase() + p2;
  }); // The word "the" must always be upper case in headings.
  top_lines = top_lines.replace(/--/g, ', '); // Replace long-dashes with ", "
  top_lines = top_lines.replace(/\\N/g, '\\n'); // If there is a \n escape code, it should stay lower-case

  return top_lines;
}

function FixSpecialAcronymCases(text) {
  text = text.replace(/shmha/i, 'SHMHA'); // A common acronym in the Minutes. Might have gotten lowercased in global All-Caps-removal

  return text;
}

function CleanDateLine(date_expression) {
  let dp = date_range_extractor(date_expression); // date properties object, or null if not parseable

  // If not parseable, return the expression unmodified.
  if (!dp) {
    return date_expression;
  }

  // At this point there are 4 possible legal variants of the date range: multi-year, multi-month, multi-day, or single-day:

  let multi_year = dp.year_last && dp.year_last !== dp.year_first;
  let multi_mon = dp.month_last && dp.month_last !== dp.month_first;
  let multi_day = dp.day_last && dp.day_last !== dp.day_first;

  let formatted = '';
  switch (true) {
    case multi_year: // [Sunday, ]December 31, 2017 - [Monday, ]January 1, 2018
      formatted =
        (dp.wkday_first || '') +
        dp.month_first +
        ' ' +
        dp.day_first +
        ', ' +
        dp.year_first +
        ' - ' +
        ((dp.wkday_last || '') +
          dp.month_last +
          ' ' +
          dp.day_last +
          ', ' +
          dp.year_last);
      break;

    case multi_mon: // [Saturday, ]January 31 - [Sunday, ]February 1, 2017
      formatted =
        (dp.wkday_first || '') +
        dp.month_first +
        ' ' +
        dp.day_first +
        ' - ' +
        ((dp.wkday_last || '') +
          dp.month_last +
          ' ' +
          dp.day_last +
          ', ' +
          dp.year_last);
      break;

    case multi_day: // January 15-16, 2017  (don't mention weekday names)
      formatted =
        dp.month_first +
        ' ' +
        dp.day_first +
        '-' +
        dp.day_last +
        ', ' +
        dp.year_first;
      break;

    default:
      // [Friday, ]March 25, 2017
      formatted =
        (dp.wkday_first || '') +
        dp.month_first +
        ' ' +
        dp.day_first +
        ', ' +
        dp.year_first;
      break;
  }
  return formatted;
}



// WEBPACK FOOTER //
// ./src/clean_minutes_markup.js